import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';
import { downloadExport } from '@/services/school.api';

export interface HoFeesShareRow {
  id: string;
  schoolId: string;
  schoolName: string;
  amount: number;
  date: string;
  paymentMethod: string | null;
  referenceNo: string | null;
  notes: string | null;
  attachmentUrl: string | null;
  sessionYear: string | null;
  createdAt: string;
}

export interface HoFeesShareListParams {
  page?: number;
  limit?: number;
  schoolId?: string;
  sessionYear?: string;
}

export interface CreateHoFeesSharePayload {
  schoolId?: string;
  amount: number;
  date: string;
  paymentMethod?: string;
  referenceNo?: string;
  notes?: string;
  attachmentUrl?: string;
  sessionYear?: string;
}

export interface HoFeesShareSummary {
  sessionYear: string;
  totalFees: number;
  headOfficeSharePercent: number;
  headOfficeShare: number;
  paymentsSent: number;
  balance: number;
}

export interface HoFeesShareAcademicYear {
  id: string;
  name: string;
  isCurrent: boolean;
}

export interface HoFeesShareOrgRow {
  schoolId: string;
  schoolName: string;
  totalFees: number;
  headOfficeShare: number;
  paymentsSent: number;
  balance: number;
}

export interface HoFeesShareOrgOverview {
  sessionYear: string;
  headOfficeSharePercent: number;
  totalHeadOfficeShare: number;
  totalPaymentsReceived: number;
  totalBalance: number;
  rows: HoFeesShareOrgRow[];
}

export interface SchoolFeesSummaryRow {
  schoolId: string;
  schoolName: string;
  totalFee: number;
  paidFee: number;
  dueFee: number;
}

export interface SchoolFeesClassSummaryRow {
  className: string;
  studentCount: number;
  totalFee: number;
  paidFee: number;
  dueFee: number;
}

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

/** Historical ledger + manual entry — no auto-calculation. See HoFeesShareService's doc comment on the backend. */
export const HoFeesShareApi = {
  list: async (params?: HoFeesShareListParams): Promise<PaginatedResult<HoFeesShareRow>> =>
    unwrap(await api.get('/ho-fees-share', { params })),

  create: async (payload: CreateHoFeesSharePayload): Promise<HoFeesShareRow> =>
    unwrap(await api.post('/ho-fees-share', payload)),

  /** School-scoped only — see HoFeesShareService.summary()'s doc comment. */
  summary: async (sessionYear: string): Promise<HoFeesShareSummary> =>
    unwrap(await api.get('/ho-fees-share/summary', { params: { sessionYear } })),

  /** School-scoped only. */
  academicYears: async (): Promise<HoFeesShareAcademicYear[]> =>
    unwrap(await api.get('/ho-fees-share/academic-years')),

  /** Cross-school — distinct session-year names across schools in scope. */
  sessionYearsForOrg: async (): Promise<string[]> => unwrap(await api.get('/ho-fees-share/org/session-years')),

  /** "Head Office Fees" — per-school 30% share vs payments received, across schools in scope. */
  overviewForOrg: async (sessionYear: string): Promise<HoFeesShareOrgOverview> =>
    unwrap(await api.get('/ho-fees-share/org/overview', { params: { sessionYear } })),

  /** "School Summary" — per-school total/paid/due student fees, across schools in scope. */
  schoolSummaryForOrg: async (sessionYear: string): Promise<SchoolFeesSummaryRow[]> =>
    unwrap(await api.get('/ho-fees-share/org/school-summary', { params: { sessionYear } })),

  /** "View School Details" — per-class Total/Paid/Due breakdown for one school + session year. */
  classSummaryForOrg: async (schoolId: string, sessionYear: string): Promise<SchoolFeesClassSummaryRow[]> =>
    unwrap(await api.get('/ho-fees-share/org/school-class-summary', { params: { schoolId, sessionYear } })),

  /** Per-student fee breakdown PDF for one class — the "Print" link on each row of the modal above. */
  printClassFeeDemand: (schoolId: string, sessionYear: string, className: string): Promise<void> =>
    downloadExport('/ho-fees-share/org/school-class-summary/print', `class-fee-demand-${className}`, 'pdf', {
      schoolId,
      sessionYear,
      className,
    }),
};
